'use client';

import NextLink from 'next/link';

import { type CSSProperties, memo, useMemo } from 'react';

import {
    Button,
    Flex,
    Grid,
    GridItem,
    Heading,
    Link,
    LinkBox,
    LinkOverlay,
    Text,
    VStack,
} from '@chakra-ui/react';

import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';

import { ListingTile } from '@nocowanie/noc-api';

import { dateHelpers, urlHelpers } from '@nocowanie/core/helpers';
import { Badge } from '@nocowanie/common-ui/components/atoms/badge';
import { defaultGuestsPickerConfig } from '@nocowanie/common-ui/components/molecules/guests-picker';
import { IconsNocowanie } from '@nocowanie/icons/nocowanie';

import { CssTooltip } from '@app/components/atoms/css-tooltip';
import { GuestsNightsLabel } from '@app/components/atoms/guests-nights-label';
import { AccommodationTileProps } from '@app/components/molecules/accommodation-tile/accommodation-tile.props';
import { AccommodationTileAmenities } from '@app/components/molecules/accommodation-tile/accommodation-tile-amenities';
import { AccommodationTileBadges } from '@app/components/molecules/accommodation-tile/accommodation-tile-badges';
import { AccommodationTileImage } from '@app/components/molecules/accommodation-tile/accommodation-tile-image';
import { AccommodationTileLocation } from '@app/components/molecules/accommodation-tile/accommodation-tile-location';
import { AccommodationTileRatePlan } from '@app/components/molecules/accommodation-tile/accommodation-tile-rate-plan';
import { AccommodationTileRoomInfo } from '@app/components/molecules/accommodation-tile/accommodation-tile-room-info';
import { Price } from '@app/components/molecules/price';
import { Rating } from '@app/components/molecules/rating';
import { LocationTypeEnum, ReservationFeaturesEnum } from '@app/enums';
import { environment } from '@app/environments/environment';
import * as accommodationHelpers from '@app/helpers/accommodation.helpers';
import { mapProvisionToTypes } from '@app/helpers/filter-mapping.helpers';
import { mapSearchStateToUrlParams } from '@app/helpers/search-params.helpers';
import { Currency } from '@app/models/api/api-types';
import { RatingDescription } from '@app/models/rating-description.model';

const AccommodationTileComponent = ({
    searchCriteria = {},
    searchFilters = {},
    position,
    amenitiesMaxVisible,
    isMobileBrowser,
    isESI,
    priorityCount = Math.max(
        environment.HIGH_PRIORITY_IMAGE_COUNT_MOBILE,
        environment.HIGH_PRIORITY_IMAGE_COUNT_DESKTOP,
    ),
    initialData = null,
    t,
    styles,
    cityLocation,
    locationVarieties,
    ...rest
}: AccommodationTileProps) => {
    const accommodationData = useMemo(() => initialData ?? null, [initialData]);

    // withHighPriority - should have different value for desktop and mobile
    // (WHEN ESI tiles are no longer supported, cause right now it generates issues)
    const highPriorityCount = isMobileBrowser
        ? environment.HIGH_PRIORITY_IMAGE_COUNT_MOBILE
        : environment.HIGH_PRIORITY_IMAGE_COUNT_DESKTOP;
    const withHighPriority =
        !isESI && isMobileBrowser ? position <= highPriorityCount : position <= priorityCount;

    const dates: {
        checkInDate: null | string;
        checkOutDate: null | string;
    } = useMemo(() => {
        if (!(searchCriteria.checkInDate && searchCriteria.checkOutDate)) {
            return {
                checkInDate: null,
                checkOutDate: null,
            };
        }

        const handleTDate = (date: string) => (date.includes('T') ? date.split('T')[0] : date);

        if (
            accommodationData?.rate?.checkIn &&
            accommodationData?.rate?.checkOut &&
            (accommodationData?.rate?.checkIn !== searchCriteria.checkInDate ||
                accommodationData?.rate?.checkOut !== searchCriteria.checkOutDate)
        ) {
            return {
                checkInDate: handleTDate(accommodationData.rate.checkIn),
                checkOutDate: handleTDate(accommodationData.rate.checkOut),
            };
        }

        return {
            checkInDate: handleTDate(searchCriteria.checkInDate),
            checkOutDate: handleTDate(searchCriteria.checkOutDate),
        };
    }, [
        accommodationData?.rate?.checkIn,
        accommodationData?.rate?.checkOut,
        searchCriteria.checkInDate,
        searchCriteria.checkOutDate,
    ]);

    const withDates = dates.checkInDate && dates.checkOutDate;

    const urls = useMemo(() => {
        if (isNil(accommodationData)) {
            return {
                accommodation: '',
                map: '',
            };
        }

        const { provision, reservationFeatures, ...filters } = searchFilters;
        const { guests, locationType, flexDates, checkInDate, checkOutDate, ...criteria } =
            searchCriteria;

        const freeCancel = reservationFeatures?.includes(ReservationFeaturesEnum.FreeCancellation);
        const withoutPrepayment = reservationFeatures?.includes(
            ReservationFeaturesEnum.WithoutPrepayment,
        );

        const provisionParams = () => {
            const mappedProvision = mapProvisionToTypes(provision?.map(v => parseInt(v)));
            if (!mappedProvision?.length) {
                return {};
            }

            return mappedProvision.reduce(
                (acc, provisionId, index) => {
                    acc[`provision[${index}]`] = provisionId.toString();
                    return acc;
                },
                {} as Record<string, string>,
            );
        };

        const adultsCount =
            guests?.adultsCount ?? (defaultGuestsPickerConfig.adultsConfig.default as number);
        const childrenCount =
            guests?.childrenCount ?? (defaultGuestsPickerConfig.childrenConfig.default as number);
        const withDefaultGuests =
            (typeof guests?.adultsCount === 'undefined' &&
                typeof guests?.childrenCount === 'undefined') ||
            (guests.adultsCount === defaultGuestsPickerConfig.adultsConfig.default &&
                guests?.childrenCount === defaultGuestsPickerConfig.childrenConfig.default);
        const accommodationUrl =
            accommodationData?.id && accommodationData?.name
                ? urlHelpers.getAccommodationUrl(
                      accommodationData.id,
                      accommodationData.name,
                      accommodationHelpers.clearFalseParams({
                          // Don't pass default guests count - it's important for pretty urls (SEO)
                          ...(withDefaultGuests ? undefined : guests),
                          ...dates,
                          flexDates,
                          ...(freeCancel && { freeCancel: 1 }),
                          ...(withoutPrepayment && { withoutPrepayment: 1 }),
                          ...provisionParams(),
                      }),
                  )
                : '';

        const params = mapSearchStateToUrlParams(
            {
                ...criteria,
                locationType: LocationTypeEnum.Accommodation,
                ...dates,
                guests: withDefaultGuests
                    ? undefined
                    : {
                          adultsCount,
                          childrenCount,
                          childrenAges: guests?.childrenAges ?? [],
                          guestCount: adultsCount + childrenCount,
                      },
                flexDates,
                geoId: accommodationData.id,
                location: accommodationData.name,
            },
            {
                ...filters,
                provision,
                reservationFeatures,
            },
        );

        return {
            map: urlHelpers.getMapUrl(params),
            accommodation: accommodationUrl,
        };
    }, [accommodationData, dates, searchCriteria, searchFilters]);

    const nightsCount = useMemo(
        () =>
            accommodationData?.rate?.checkIn && accommodationData?.rate?.checkOut
                ? Math.max(
                      1,
                      dateHelpers.daysDateDiff(
                          new Date(accommodationData?.rate?.checkIn),
                          new Date(accommodationData?.rate?.checkOut),
                      ),
                  )
                : 1,
        [accommodationData?.rate?.checkIn, accommodationData?.rate?.checkOut],
    );

    const isInquiry = accommodationData?.source === ListingTile.source.NOC;
    const isOTA = accommodationData?.source === ListingTile.source.NOP;

    if (!accommodationData) {
        return null;
    }

    const {
        id,
        name,
        images,
        features,
        path,
        source,
        listing: { placement = undefined } = {},
        location: { settlement = undefined, latitude, longitude } = {},
        rating,
        rate,
    } = accommodationData;

    const coordinates = latitude && longitude ? { lat: latitude, lon: longitude } : undefined;

    // Do not display discount info when dates are not set
    const basePrice = !withDates ? rate?.convertedRate : rate?.convertedBaseRate;
    const beachDistanceMeters = accommodationData?.distances?.beach?.meters;

    return (
        <LinkBox asChild>
            <Grid
                as={'article'}
                data-testid={`acc-tile-${accommodationData?.source}`}
                data-id={id}
                css={styles.container}
                {...rest}
                style={rest.style as CSSProperties | undefined}
            >
                <GridItem data-testid={'acc-tile-images'} css={styles.images}>
                    <AccommodationTileImage
                        accommodationName={name ?? ''}
                        images={images ?? []}
                        withHighPriority={withHighPriority}
                        isOTA={isOTA}
                    />
                </GridItem>

                <GridItem data-testid={'acc-tile-info'} css={styles.info}>
                    <AccommodationTileBadges
                        source={source}
                        reservationType={features?.internalLabel?.reservationType}
                        isBestseller={features?.bestseller}
                        listingPlacement={placement}
                        t={t}
                    />
                    <Flex gap={0.5} mb={-2} alignItems={'start'}>
                        {features?.flagged ? (
                            <CssTooltip
                                content={t('badges.tooltips.flagged')}
                                zIndex={'overLinkOverlay'}
                                tooltipProps={{
                                    w: '300px',
                                    maxW: '300px',
                                    hideBelow: 'md',
                                }}
                            >
                                <IconsNocowanie.Sponsored
                                    accentFillColor={'selectiveYellow'}
                                    fontSize={'lg'}
                                    css={{
                                        '--icon-stroke': 1,
                                        verticalAlign: {
                                            base: 'top',
                                            md: 'middle',
                                        },
                                    }}
                                />
                            </CssTooltip>
                        ) : null}
                        <Heading
                            as={'h3'}
                            data-testid={'acc-tile-name'}
                            size={'apartmentTile'}
                            mr={1}
                            mb={{
                                base: 1.5,
                                md: 0.5,
                            }}
                            lineClamp={{
                                base: 2,
                                md: 1,
                            }}
                        >
                            <LinkOverlay asChild>
                                {isInquiry ? (
                                    <Link
                                        href={path}
                                        variant={'blendIn'}
                                        color={'inherit'}
                                        display={'inline'}
                                        dangerouslySetInnerHTML={{
                                            __html: name || '',
                                        }}
                                    />
                                ) : (
                                    <Link
                                        variant={'blendIn'}
                                        color={'inherit'}
                                        display={'inline'}
                                        asChild
                                    >
                                        <NextLink
                                            href={urls.accommodation}
                                            prefetch={false}
                                            dangerouslySetInnerHTML={{
                                                __html: name || '',
                                            }}
                                        />
                                    </Link>
                                )}
                            </LinkOverlay>
                        </Heading>
                    </Flex>
                    {id && placement && name && settlement?.name ? (
                        <AccommodationTileLocation
                            accommodation={{
                                id,
                                listingPlacement: placement,
                                name,
                                settlementName: settlement.name,
                                isOTA,
                                ...(coordinates && { coordinates }),
                            }}
                            searchCriteria={searchCriteria}
                            mapUrl={urls.map}
                            beachDistanceMeters={beachDistanceMeters}
                            cityLocation={cityLocation}
                            varietyGenitive={
                                locationVarieties?.genitive?.length
                                    ? locationVarieties?.genitive
                                    : locationVarieties?.nominative
                            }
                            t={t}
                            isESI={isESI}
                        />
                    ) : null}
                </GridItem>
                <GridItem
                    data-testid={'acc-tile-rating'}
                    css={styles.rating}
                    mb={rating?.ratingsAverage ? undefined : -3}
                >
                    {rating?.ratingsAverage && rating?.ratingCount && rating?.ratingDescription ? (
                        <Rating
                            ratingAverage={rating.ratingsAverage}
                            ratingCount={rating.ratingCount}
                            ratingDescription={
                                rating.ratingDescription as unknown as RatingDescription
                            }
                            ratingProps={{
                                pt: 0.5,
                                minW: 'auto',
                                size: 'accommodationTile',
                            }}
                            variant={'accommodationTile'}
                            t={t}
                        />
                    ) : null}
                </GridItem>
                <GridItem data-testid={'acc-tile-amenities'} css={styles.amenities} minW={0}>
                    <AccommodationTileAmenities
                        accommodationId={id || ''}
                        amenities={features?.internalAmenityGroups}
                        t={t}
                        maxVisible={amenitiesMaxVisible}
                        mt={{
                            md: isInquiry ? -6 : undefined,
                        }}
                    />
                </GridItem>
                <GridItem
                    data-testid={'acc-tile-details'}
                    css={styles.details}
                    mb={{
                        base: isInquiry ? -2 : undefined,
                        md: undefined,
                    }}
                >
                    {isInquiry ? (
                        features?.promoText ? (
                            <Text data-testid={'acc-tile-promo-text'} css={styles.promoText}>
                                {features.promoText}
                            </Text>
                        ) : null
                    ) : null}

                    {isOTA && rate ? (
                        <>
                            {rate?.apartment ? (
                                <AccommodationTileRoomInfo
                                    apartment={rate.apartment}
                                    t={t}
                                    mb={2}
                                />
                            ) : null}
                            <AccommodationTileRatePlan rate={rate} t={t} />
                        </>
                    ) : undefined}
                </GridItem>
                <GridItem
                    as={VStack}
                    css={styles.price}
                    pb={{
                        base: isInquiry ? 2 : undefined,
                        md: undefined,
                    }}
                >
                    {isInquiry ? (
                        <Text
                            data-testid={'acc-tile-price-confirmation'}
                            css={styles.priceConfirmation}
                        >
                            {t('priceConfirmationInfo')}
                        </Text>
                    ) : (
                        <>
                            {!!Number(searchCriteria.flexDates) &&
                            rate?.checkIn &&
                            rate?.checkOut ? (
                                <Badge
                                    variant={'outline'}
                                    colorPalette={'gray'}
                                    size={'sm'}
                                    fontWeight={'normal'}
                                    alignSelf={'end'}
                                >
                                    {dateHelpers.formatDateRange(rate.checkIn, rate.checkOut)}
                                </Badge>
                            ) : null}
                            {rate ? (
                                <Price
                                    currency={rate?.convertedCurrency ?? Currency.Pln}
                                    price={rate?.convertedRate ?? 0}
                                    basePrice={basePrice ?? 0}
                                    discountValue={withDates ? rate?.discount : undefined}
                                    variant={'accommodationTile'}
                                    withFlexDates={!!searchCriteria.flexDates}
                                    pb={{ base: 2, md: 0 }}
                                    alignSelf={'flex-end'}
                                    priceFromLabel={!withDates ? t('priceFrom') : undefined}
                                    discountProps={{
                                        py: 1.5,
                                    }}
                                >
                                    <GuestsNightsLabel
                                        adultsCount={searchCriteria.guests?.adultsCount ?? 2}
                                        childrenCount={searchCriteria.guests?.childrenCount ?? 0}
                                        nightsCount={nightsCount}
                                        t={t}
                                        withListingTile
                                    />
                                </Price>
                            ) : null}
                        </>
                    )}

                    <Button
                        className={'accommodation-tile-cta'}
                        data-testid={'acc-tile-cta'}
                        colorPalette={'secondary'}
                        px={5}
                        zIndex={-1}
                        hideBelow={'md'}
                        fontSize={'sm'}
                        height={10}
                    >
                        {t(isInquiry ? 'ctaInquiry' : 'ctaOTA')}
                    </Button>
                </GridItem>
            </Grid>
        </LinkBox>
    );
};

const areTilePropsEqual = (prev: AccommodationTileProps, next: AccommodationTileProps) => {
    const isEqualResult =
        prev.initialData === next.initialData &&
        prev.position === next.position &&
        prev.priorityCount === next.priorityCount &&
        prev.amenitiesMaxVisible === next.amenitiesMaxVisible &&
        prev.isMobileBrowser === next.isMobileBrowser &&
        prev.isESI === next.isESI &&
        isEqual(prev.cityLocation, next.cityLocation) &&
        isEqual(prev.locationVarieties, next.locationVarieties) &&
        isEqual(prev.searchCriteria, next.searchCriteria) &&
        isEqual(prev.searchFilters, next.searchFilters) &&
        prev.styles === next.styles &&
        prev.t === next.t;

    return isEqualResult;
};

export const AccommodationTile = memo(AccommodationTileComponent, areTilePropsEqual);
